import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import { keys } from "./query";
import type { Permission } from "@clubghor/auth";

export interface SessionUser {
  id: number;
  email: string;
  name: string;
  role: string;
  roleLabel: string;
}

interface SessionPayload {
  user: SessionUser;
  permissions: Permission[];
}

interface SessionValue {
  user: SessionUser | null;
  permissions: Permission[];
  /** True while the session is being resolved on first paint. */
  isLoading: boolean;
  /** Drives the sign-in screen without flashing content for a signed-out user. */
  isAuthenticated: boolean;
  /**
   * True when /me answered 401: the session is genuinely gone. A network or 5xx
   * failure sets `hasError` instead, so the auth gate can retry rather than
   * bouncing a signed-in operator to /login during a blip.
   */
  isAuthError: boolean;
  hasError: boolean;
  /**
   * Permission check for the UI. Hiding an action the role cannot perform is
   * better than offering a button that returns 403, but it is NOT the security
   * boundary: every route re-checks server-side.
   */
  can: (permission: Permission) => boolean;
  signOut: () => Promise<void>;
  refresh: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: keys.session,
    queryFn: () => api.get<SessionPayload>("/api/auth/me"),
    // A 401 is an answer, not a failure to retry.
    retry: false,
    staleTime: 5 * 60_000,
  });

  const isAuthError = query.error instanceof ApiError && query.error.isAuthError;

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      // Clear the cache regardless: one session's data must never survive into
      // the next sign-in, which could be a different account entirely.
      queryClient.clear();
      window.location.assign("/login");
    }
  }, [queryClient]);

  const value = useMemo<SessionValue>(() => {
    const user = query.data?.user ?? null;
    const permissions = query.data?.permissions ?? [];

    return {
      user,
      permissions,
      isLoading: query.isLoading,
      isAuthenticated: Boolean(user) && !isAuthError,
      isAuthError,
      hasError: query.isError,
      can: (permission) => permissions.includes(permission),
      signOut,
      refresh: () => {
        void query.refetch();
      },
    };
    // `query` itself is deliberately not a dependency: it is a fresh object on
    // every render, which used to rebuild this value (and every `can()` closure
    // downstream) each render, re-rendering all session consumers in lockstep.
  }, [query.data, query.isLoading, query.isError, isAuthError, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside a SessionProvider");
  return context;
}

/** Convenience for the common case of gating one control. */
export function useCan(permission: Permission): boolean {
  return useSession().can(permission);
}
